/** Finance review of who pays whom: migrated records stay root-paid until Finance approves a change. */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { StatusPill, btnPrimary, btnSmall, errorText, inputCls } from '../finance/financeUi';

const MODE_LABEL = { ROOT_PAYER: 'Root pays (e.g. MSC Managed)', IMMEDIATE_PARENT: 'Immediate parent pays (ISO / Parent Managed)', CUSTOM_PAYER: 'Custom payer (an economic ancestor)' };

function ProposeForm({ row, options, onDone }) {
  const [f, setF] = useState({ payout_mode: 'IMMEDIATE_PARENT', commercial_relationship: row.commercial_relationship || 'UNCLASSIFIED', custom_payer_user_id: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      await api.request(`/payout-routing/review/${row.relationship_id}/propose`, { method: 'POST', body: JSON.stringify({ ...f, custom_payer_user_id: f.custom_payer_user_id || null }) });
      toast.success(`Proposal saved — would take effect ${options.next_effective_period} after approval`); onDone();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-2 rounded-lg border bg-muted/30 p-3 sm:grid-cols-2" data-testid={`propose-form-${row.relationship_id}`}>
      <select className={inputCls} value={f.payout_mode} onChange={set('payout_mode')} data-testid="propose-payout-mode">
        {options.payout_modes.map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
      </select>
      <select className={inputCls} value={f.commercial_relationship} onChange={set('commercial_relationship')} data-testid="propose-commercial-relationship">
        {options.commercial_relationships.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
      </select>
      {f.payout_mode === 'CUSTOM_PAYER' && <input className={inputCls} placeholder="Payer user id (must be an ancestor)" value={f.custom_payer_user_id} onChange={set('custom_payer_user_id')} data-testid="propose-custom-payer" />}
      <input className={inputCls} placeholder="Reason (required)" value={f.reason} onChange={set('reason')} data-testid="propose-reason" />
      <button type="button" className={btnPrimary} disabled={busy || !f.reason.trim()} onClick={submit} data-testid="propose-submit-btn">Save proposal</button>
    </div>
  );
}

function ReviewRow({ row, options, onChange }) {
  const [open, setOpen] = useState(false);
  const act = async (path, msg) => {
    try { await api.request(path, { method: 'POST', body: JSON.stringify({}) }); toast.success(msg); onChange(); } catch (e) { toast.error(errorText(e)); }
  };
  const p = row.proposed;
  return (
    <li className="space-y-2 px-4 py-3" data-testid={`review-row-${row.party_user_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 text-sm">
        <div className="min-w-0">
          <p className="font-medium">{row.user_name} <span className="text-xs text-muted-foreground">· {row.role} · {row.tenant_name}</span></p>
          <p className="text-xs text-muted-foreground">Economic parent: {row.economic_parent} · Current payer: <b data-testid={`current-payer-${row.party_user_id}`}>{row.current_payer}</b> ({row.current_payout_label}) · {String(row.commercial_relationship || '').replace(/_/g, ' ')}</p>
          {p && <p className="text-xs" data-testid={`proposed-${row.party_user_id}`}>Proposed: <b>{p.payer}</b> ({p.payout_label}) from {p.effective_from} — {p.reason}</p>}
          {row.scheduled && <p className="text-xs text-emerald-700" data-testid={`scheduled-${row.party_user_id}`}>Approved: {row.scheduled.payer} from {row.scheduled.effective_from} · applies in payer Payout Runs once this tenant's routing is authoritative</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill value={row.review_status} testid={`review-status-${row.party_user_id}`} />
          {p ? (
            <>
              <button type="button" className={btnSmall} onClick={() => act(`/payout-routing/proposals/${p.relationship_id}/approve`, `Approved — effective ${options.next_effective_period}, comparison only`)} data-testid={`approve-proposal-${row.party_user_id}`}>Approve</button>
              <button type="button" className={btnSmall} onClick={() => act(`/payout-routing/proposals/${p.relationship_id}/reject`, 'Proposal rejected')} data-testid={`reject-proposal-${row.party_user_id}`}>Reject</button>
            </>
          ) : (
            <>
              {row.review_status === 'NEEDS_REVIEW' && <button type="button" className={btnSmall} onClick={() => act(`/payout-routing/review/${row.relationship_id}/confirm`, 'Confirmed — stays root-paid')} data-testid={`confirm-current-${row.party_user_id}`}>Keep current payer</button>}
              <button type="button" className={btnSmall} onClick={() => setOpen(!open)} data-testid={`propose-btn-${row.party_user_id}`}>Propose change</button>
            </>
          )}
        </div>
      </div>
      {open && !p && <ProposeForm row={row} options={options} onDone={() => { setOpen(false); onChange(); }} />}
    </li>
  );
}

export function ReviewQueue({ rows, options, onChange }) {
  if (!rows.length) return <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="review-empty">No payout relationships yet — run the migration preview above.</p>;
  return <ul className="divide-y" data-testid="review-queue">{rows.map((r) => <ReviewRow key={r.relationship_id} row={r} options={options} onChange={onChange} />)}</ul>;
}
